import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { createInviteLink, listInvites, sendInvite } from '../../api/invites';

const INVITES_UNAVAILABLE = 'Invites are not available yet. You can still write.';

function inviteError(message) {
  const text = String(message || '');
  if (!text || /internal error/i.test(text)) return INVITES_UNAVAILABLE;
  return text;
}

const STATUS_LABEL = {
  created: 'Link ready',
  sent: 'Email sent',
  signed_up: 'Signed up',
};

function inviteHref(invite) {
  if (invite.url) return invite.url;
  return `${window.location.origin}${invite.path}`;
}

function InviteWriter() {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [latestLink, setLatestLink] = useState('');
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get('/users/me');
        if (cancelled) return;
        if (me?.invites_enabled !== true) return;
        setAllowed(true);
        const data = await listInvites();
        if (cancelled) return;
        if (data?.available === false) {
          setInvites([]);
          setError(inviteError(data.message));
          return;
        }
        setInvites(data?.invites || []);
      } catch (err) {
        if (!cancelled) setError(inviteError(err.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const remember = (invite) => {
    setInvites((current) => [invite, ...current.filter((row) => row.id !== invite.id)]);
    setLatestLink(inviteHref(invite));
    setNotice(invite.message || '');
  };

  const onCreate = async () => {
    setBusy(true);
    setError('');
    try {
      const invite = await createInviteLink();
      remember(invite);
    } catch (err) {
      setError(inviteError(err.message) || 'The link was not created.');
    } finally {
      setBusy(false);
    }
  };

  const onSend = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const invite = await sendInvite(email.trim());
      remember(invite);
      setEmail('');
    } catch (err) {
      setError(inviteError(err.message) || 'The invite was not sent.');
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice('Copied.');
    } catch {
      setNotice('Select the link and copy it.');
    }
  };

  if (!allowed) return null;

  return (
    <section className="settings-section" aria-labelledby="invite-writer-heading">
      <h3 id="invite-writer-heading">Invite a writer</h3>
      <p className="settings-section-description">
        Create a link you can send, or send it to an email address. A new writer still needs an invitation to create an account.
      </p>
      <div className="invite-writer">
        <div className="invite-writer__row">
          <button type="button" onClick={onCreate} disabled={busy}>
            Create a link
          </button>
        </div>
        <form className="invite-writer__row" onSubmit={onSend}>
          <label>
            Email address
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <button type="submit" className="invite-writer__primary" disabled={busy}>
            Send invite
          </button>
        </form>
        {latestLink ? (
          <label>
            Invite link
            <input readOnly value={latestLink} />
          </label>
        ) : null}
        {latestLink ? (
          <div className="invite-writer__row">
            <button type="button" onClick={() => onCopy(latestLink)}>
              Copy link
            </button>
          </div>
        ) : null}
        {notice ? <p className="invite-writer__note">{notice}</p> : null}
        {error ? <p className="settings-error" role="alert">{error}</p> : null}
        {loading ? <p className="invite-writer__status">Loading invites…</p> : null}
        {invites.length > 0 ? (
          <ul className="invite-writer__list">
            {invites.map((invite) => (
              <li key={invite.id} className="invite-writer__item">
                <span>{invite.email || 'Link'}</span>
                <span>{STATUS_LABEL[invite.status] || invite.status}</span>
                {invite.status !== 'signed_up' ? (
                  <button type="button" onClick={() => onCopy(inviteHref(invite))}>
                    Copy link
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}

export default InviteWriter;
